import {
    defaultDisplayConfigs,
    type CrmDisplayConfigContact,
    type CrmDisplayConfigField,
} from '@plato/data/model/crm-config';
import type {
    CrmCustomerContactUpdate,
    CrmCustomerContactUpdateInput,
} from '@plato/data/queries/crm-customer-contact-update';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { Dialog } from '@plato/ui/containers/dialog';
import { cn } from '@plato/ui/util/classes';
import { useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useCrmConfig } from '../hooks/use-crm-config.ts';
import type { CrmContext } from './crm-context.tsx';
import { CrmContextProvider, useCrmFieldConfig, useDraftData } from './crm-context.tsx';
import { CrmFieldValue, getFieldKey } from './crm-field.tsx';
import { CrmFieldLabel } from './crm-label.tsx';

export type EditContactDialogProps = {
    contactId: string;
    customerId: string;
};

export const EditContactDialog = DialogStateManager.create<EditContactDialogProps>(
    ({ contactId, customerId }) => {
        const { remove: closeDialog, visible: isDialogVisible } = useDialog();
        const { toast } = useToast();
        const { t } = useTranslation('customer-detail');
        const sendEvent = useAnalytics();
        const { data: crmConfig } = useCrmConfig();
        const { crmCustomFieldsConfig, crmDisplayConfig } = crmConfig ?? {};
        const apiUtils = api.useUtils();

        const { data: apiData, isLoading: isContactLoading } =
            api.platoUser.getDetailCustomerContact.get.useQuery({
                contactId,
                customerId,
            });

        const { mutate: apiSaveContact, isPending: isContactSaving } =
            api.platoUser.command.customers.updateContact.useMutation({
                onError: async () => {
                    toast({ icon: 'warning', title: t('save_error') });
                    reset();
                },

                onSuccess: async () => {
                    toast({ icon: 'success', title: t('save_successful') });
                    void apiUtils.platoUser.getDetailCustomerContact.get.invalidate();
                    void apiUtils.platoUser.getManyCustomerContact.get.invalidate();

                    sendEvent('customer_contact_edited', {
                        customer_id: customerId,
                        contact_id: contactId,
                    });

                    closeDialog();
                },
            });

        const saveContact = useCallback(
            (data: CrmCustomerContactUpdateInput) => {
                apiSaveContact({
                    contactId: data.contactId,
                    customerId: data.customerId,
                    birthday: data.birthday?.trim() || undefined,
                    comment: data.comment?.trim() || undefined,
                    department: data.department?.trim() || undefined,
                    role: data.role?.trim() || undefined,
                    customFields: data.customFields || undefined,
                });
            },
            [apiSaveContact],
        );

        const { data, reset, save, setData } = useDraftData<CrmCustomerContactUpdate>({
            data: apiData,
            disableAutoSave: true,
            save: saveContact,
        });

        const context = useMemo(
            (): CrmContext<'contact'> => ({
                data,
                entityType: 'contact',
                customFieldConfig: crmCustomFieldsConfig,
                setData,
            }),
            [data, crmCustomFieldsConfig, setData],
        );

        const config: CrmDisplayConfigContact = crmDisplayConfig?.contact ?? defaultDisplayConfigs.contact;
        const fields = config.editFields ?? config.fields ?? [];
        const anyEditable = fields.some((x) => x.writable);

        return (
            <Dialog open={isDialogVisible} onClose={closeDialog} disablePointerDismissal>
                <DialogForm className="sm:max-w-2xl">
                    <DialogForm.Header
                        title={t('contact_edit__modal_title')}
                        subtitle={t('contact_edit__modal_subtitle')}
                    />
                    <DialogForm.Content>
                        <CrmContextProvider value={context}>
                            <fieldset
                                className="grid grid-cols-2 gap-4 w-full p-0"
                                disabled={isContactLoading || isContactSaving}
                            >
                                {fields.map((field) => (
                                    <CrmField key={getFieldKey(field)} config={field} />
                                ))}
                            </fieldset>
                        </CrmContextProvider>
                    </DialogForm.Content>
                    <DialogForm.SubmitBlock
                        onCancel={closeDialog}
                        onSubmit={save}
                        submitText={t('contact_edit__action__save')}
                        disabled={!anyEditable || isContactLoading}
                        isLoading={isContactSaving}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);

type CrmFieldProps = {
    config: CrmDisplayConfigField;
};

function CrmField(props: CrmFieldProps) {
    const config = useCrmFieldConfig(props.config);
    const id = useId();
    const isCompact = config.compact ?? false;

    return (
        <div className={cn('flex flex-col gap-2', !isCompact && 'col-span-2')}>
            <CrmFieldLabel htmlFor={id}>{config.label}</CrmFieldLabel>
            <CrmFieldValue config={config} id={id} mode="edit" />
        </div>
    );
}
